/**
 * libraryDiff — pure helper that computes a per-document diff
 * between two library inventory snapshots. Spec 24 T2 (LibraryDetail
 * panel data source).
 *
 * Diff strategy:
 *   - Compare doc_id sets → added (in next, not in prior),
 *     removed (in prior, not in next).
 *   - Same doc_id with a different doc_name → renamed.
 *   - Stable sort by name so the diff is deterministic + scannable.
 *
 * SRP: pure data function. No fetching, no React. Tests pin every
 * branch (added-only, removed-only, renamed-only, mixed, empty).
 *
 * # DECISION: rename detection is doc_id based, not heuristic
 * #   string-similarity.
 * # WHY: Asthra's library backend assigns durable doc_ids (the
 * #      ResolvedClassification.doc_id field). Same id + new name =
 * #      a rename in the source-of-truth sense. String-similarity
 * #      heuristics (Levenshtein, soundex) would be ambiguous when
 * #      multiple docs are renamed at once. Honest = trust the id.
 * # DATE: 2026-04-22
 */

import type { LibraryDiffEntry } from "./eventLog";

/** Bounded view over the inventory shape we need — accepts the full
 *  ``ResolvedInventoryDTO`` as well as test fixtures. Keeps the
 *  helper free of an api/client import (DIP). */
export interface InventorySnapshot {
  classifications: ReadonlyArray<{ doc_id: string; doc_name: string }>;
}

/**
 * Compute the diff between two snapshots. Returns an empty array
 * when the snapshots are identical or when ``prior`` is null
 * (first observation has nothing to compare against).
 */
export function diffLibraryInventory(prior: InventorySnapshot | null, next: InventorySnapshot): LibraryDiffEntry[] {
  if (!prior) return [];

  const priorById = new Map<string, string>();
  for (const c of prior.classifications) {
    priorById.set(c.doc_id, c.doc_name);
  }
  const nextById = new Map<string, string>();
  for (const c of next.classifications) {
    nextById.set(c.doc_id, c.doc_name);
  }

  const out: LibraryDiffEntry[] = [];

  // Added — in next but not in prior.
  for (const [id, name] of nextById) {
    if (!priorById.has(id)) out.push({ op: "added", name });
  }
  // Removed — in prior but not in next.
  for (const [id, name] of priorById) {
    if (!nextById.has(id)) out.push({ op: "removed", name });
  }
  // Renamed — same id, different name.
  for (const [id, nextName] of nextById) {
    const priorName = priorById.get(id);
    if (priorName !== undefined && priorName !== nextName) {
      out.push({ op: "renamed", name: nextName, fromName: priorName });
    }
  }

  // Deterministic order: by op (added → removed → renamed) then by
  // name. Writers reading the diff scan top-to-bottom; predictable
  // ordering avoids "where did that go?" frustration on re-render.
  const opOrder: Record<LibraryDiffEntry["op"], number> = {
    added: 0,
    removed: 1,
    renamed: 2,
  };
  out.sort((a, b) => {
    const opCompare = opOrder[a.op] - opOrder[b.op];
    if (opCompare !== 0) return opCompare;
    return a.name.localeCompare(b.name);
  });

  return out;
}
